import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Flex, HStack, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Text, useColorModeValue } from '@bonusly/design-system';

import type { DoodleCardDraft, RecognitionDraftCard, RecognitionDraftVariant } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyChatActions, useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { DotsHorizontalOutline, TrashOutline, XOutline } from '../lib/icons';
import { RECOGNITION_DRAFT_FOCUS_TAB_PROPS } from '../lib/recognition-draft-focus-tab-props';
import { AmountPill, HashtagToken, RecipientHandle } from '../lib/recognition-segments';
import { UiBlockActionBar } from '../lib/ui-block-action-bar';
import { MakeItSpecialMenu } from './make-it-special-menu';
import type { BizyNodeProps } from './node-props';

/** Hand-rolled focus/tone tab (replaces the app's PillTab) — pill toggle styled with theme tokens. */
function FocusTab({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) {
	const activeBg = useColorModeValue('secondary.bg', 'white');
	const activeColor = useColorModeValue('text.onColor', 'gray.900');
	return (
		<Flex
			as="button"
			type="button"
			role="tab"
			aria-selected={isActive}
			onClick={onClick}
			flexShrink={0}
			align="center"
			bg={isActive ? activeBg : 'transparent'}
			borderColor={isActive ? 'transparent' : 'border.default'}
			borderRadius="full"
			borderStyle="solid"
			borderWidth="1px"
			color={isActive ? activeColor : 'text.default'}
			_hover={isActive ? { bg: activeBg } : { bg: 'secondary.bgAlpha' }}
			{...RECOGNITION_DRAFT_FOCUS_TAB_PROPS}
		>
			{label}
		</Flex>
	);
}

/**
 * Recognition draft card — the user is writing a recognition and Bizy is
 * offering a drafted message. When the payload carries multiple `variants`
 * the card shows a tab per focus; selecting one swaps the body + actions
 * client-side. A "Make it special" wand menu (replacing the copy button) lets
 * the user add a GIF (on-demand `show_gif_picker`), draw a doodle
 * (`draft_doodle`), or toggle a gift wrap on the post; an attached gif shows on
 * the card with a remove kebab.
 */
export function RecognitionDraftCardNode({ node }: BizyNodeProps) {
	const block = node as RecognitionDraftCard;
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();
	const { fontSize = 'sm', isReadOnly, wrap } = useBizyChatActions();
	const { giver, recipient, amount, message, hashtags, actions, variants, attachment, coachingPrompt, wrapPost } = block.payload;
	const [activeIndex, setActiveIndex] = useState(0);
	const [attachPending, setAttachPending] = useState(false);
	const isWrapped = !!wrapPost;

	// Post wrap: "Wrap this post" shows when the user owns a wrap; a buy
	// affordance shows when wraps are enabled but they own none.
	const activeWrap = wrap?.activeWrap ?? null;
	const canWrap = !isReadOnly && !!wrap?.enabled && !!activeWrap;
	const showBuyWrap = !isReadOnly && !!wrap?.enabled && !activeWrap && !!wrap?.onBuyWrap;

	const attachmentKey = attachment?.gif ?? null;
	const [pendingForAttachment, setPendingForAttachment] = useState(attachmentKey);
	if (pendingForAttachment !== attachmentKey) {
		setPendingForAttachment(attachmentKey);
		setAttachPending(false);
	}

	const hasTabs = (variants?.length ?? 0) > 1;
	const fallbackVariant: RecognitionDraftVariant = { focus: null, message, amount, hashtags, actions, qualityScore: null, coachingPrompt };
	const safeIndex = Math.min(activeIndex, Math.max(0, (variants?.length ?? 1) - 1));
	const active = variants?.[safeIndex] ?? fallbackVariant;

	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const activeMessage = active.message;
	const activeAmount = active.amount;
	const activeActions = active.actions ?? [];
	const activeCoachingPrompt = hasTabs ? active.coachingPrompt : (active.coachingPrompt ?? coachingPrompt);
	const hashtagList = (active.hashtags ?? []).map((h) => h.replace(/^#/, ''));

	// The draft the user currently sees (the focused variant). The gif remove
	// and the wand actions carry THIS — not the original top-level payload — so
	// the backend's pill generation / re-render matches the visible tab.
	const currentDraft: DoodleCardDraft = {
		recipient,
		amount: activeAmount ?? null,
		message: activeMessage,
		hashtags: active.hashtags ?? [],
		actions: activeActions,
		...(variants ? { variants } : {}),
		...(activeCoachingPrompt ? { coachingPrompt: activeCoachingPrompt } : {}),
		// Carry the wrap state so gif/doodle re-renders preserve it.
		...(isWrapped ? { wrapPost: true } : {}),
	};

	// Remove the attached gif (the on-card kebab): attach_gif with a null
	// attachment re-emits the draft without it.
	const handleRemoveGif = () => {
		if (attachPending) return;
		setAttachPending(true);
		dispatch({ type: 'attach_gif', payload: { ...currentDraft, attachment: null } });
	};

	// "Wrap this post" / the chip's X: dispatch a wrap_post turn (apply or remove);
	// carries the attachment so a gif survives the re-render.
	const dispatchWrap = (nextWrap: boolean) => {
		if (attachPending) return;
		dispatch({ type: 'wrap_post', payload: { ...currentDraft, wrapPost: nextWrap, attachment: attachment ?? null } });
	};

	// The "Make it special" wand menu replaces the copy button; hidden in
	// read-only views, disabled while a gif attach/remove is in flight.
	const makeItSpecialMenu = isReadOnly ? undefined : (
		<MakeItSpecialMenu
			draft={currentDraft}
			isDisabled={attachPending}
			onWrapPost={canWrap && !isWrapped ? () => dispatchWrap(true) : undefined}
			onBuyWrap={showBuyWrap ? wrap?.onBuyWrap : undefined}
		/>
	);

	// Copy text mirrors what the card shows (amount, recipients, message,
	// hashtags). The action bar prefers the wand accessory when editable and
	// falls back to this copy button in read-only views, where the wand is
	// hidden — so read-only drafts keep clipboard copy like other cards.
	const copyText = [
		activeAmount && activeAmount > 0 ? `+${activeAmount}` : null,
		...recipients.map((r) => `@${r.replace(/^@/, '')}`),
		activeMessage,
		...hashtagList.map((h) => `#${h}`),
	]
		.filter(Boolean)
		.join(' ');

	return (
		<Box my={2}>
			<Box px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
				{hasTabs && (
					<>
						<Box overflowX="auto" mb={3} fontSize="sm" lineHeight="5" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
							<HStack role="tablist" spacing={2}>
								{variants?.map((v, i) => (
									<FocusTab
										key={`${v.focus ?? 'focus'}-${i}`}
										isActive={i === safeIndex}
										onClick={() => setActiveIndex(i)}
										label={v.focus || t('uiBlocks.recognitionDraft.focusFallback', { index: i + 1 })}
									/>
								))}
							</HStack>
						</Box>
						<Divider borderColor="border.default" width="auto" mx={-3} mb={3} />
					</>
				)}
				<Text fontSize={fontSize} lineHeight="tall" whiteSpace="pre-wrap">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{activeAmount != null && activeAmount > 0 && (
						<>
							<AmountPill amount={activeAmount} />{' '}
						</>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{activeMessage}
					{hashtagList.length > 0 && (
						<>
							{' '}
							{hashtagList.map((h, i) => (
								<span key={`${h}-${i}`}>
									<HashtagToken hashtag={h} />
									{i < hashtagList.length - 1 ? ' ' : ''}
								</span>
							))}
						</>
					)}
				</Text>
				{attachment?.gif && (
					<Box mt={2} position="relative" role="group" w="fit-content" maxW="100%" mx="auto">
						<Box borderRadius="lg" overflow="hidden">
							<Image src={attachment.gif} alt={attachment.altText ?? ''} display="block" maxH="220px" maxW="100%" objectFit="contain" w="auto" />
						</Box>
						{!isReadOnly && (
							<Menu>
								<MenuButton
									as={IconButton}
									icon={<DotsHorizontalOutline boxSize={4} />}
									aria-label={t('uiBlocks.gifPicker.options')}
									position="absolute"
									top={1}
									right={1}
									size="xs"
									isRound
									variant="solid"
									bg="blackAlpha.600"
									color="white"
									_hover={{ bg: 'blackAlpha.700' }}
								/>
								<MenuList minW="150px" p={1} border="none">
									<MenuItem onClick={handleRemoveGif} borderRadius="md" color="error.fg">
										<HStack spacing={2}>
											<TrashOutline boxSize={4} />
											<Text>{t('uiBlocks.gifPicker.remove')}</Text>
										</HStack>
									</MenuItem>
								</MenuList>
							</Menu>
						)}
					</Box>
				)}
				{isWrapped && (
					<Flex mt={2} align="center" gap={2} px={2} py={1.5} border="1px solid" borderColor="border.default" borderRadius="md">
						{activeWrap?.previewUrl && (
							<Box boxSize="24px" borderRadius="md" overflow="hidden" flexShrink={0}>
								<Image src={activeWrap.previewUrl} alt="" w="full" h="full" objectFit="cover" />
							</Box>
						)}
						<Text fontSize={fontSize} fontWeight="semibold" color="text.default">
							{t('uiBlocks.postWrap.applied')}
						</Text>
						{activeWrap && (
							<Text fontSize="xs" color="text.softer">
								{t('uiBlocks.postWrap.remaining', { count: activeWrap.remainingUses, total: activeWrap.totalUses })}
							</Text>
						)}
						{!isReadOnly && (
							<IconButton
								aria-label={t('uiBlocks.postWrap.remove')}
								icon={<XOutline boxSize={4} />}
								size="xs"
								variant="ghost"
								colorScheme="gray"
								ml="auto"
								isDisabled={attachPending}
								onClick={() => dispatchWrap(false)}
							/>
						)}
					</Flex>
				)}
				<UiBlockActionBar actions={activeActions} leftAccessory={makeItSpecialMenu} copyText={copyText} areActionsDisabled={isReadOnly} />
			</Box>
			{activeCoachingPrompt && (
				<Text mt={2} fontSize={fontSize} color="text.default" lineHeight="short">
					{activeCoachingPrompt}
				</Text>
			)}
		</Box>
	);
}
